import React, { useCallback, useState, useEffect } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ACCEPT_FILE } from 'src/config';
import { useUploadApprovalDocument } from 'src/use-cases/invoice/useInvoices';
import { createAxiosInstance } from 'src/utils/createAxiosInstance';
import { attachCommonErrorInterceptor } from 'src/infra/services/interceptors';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { InvoiceSummaryDto, Attachment } from 'src/generated/services/TFinancialApi';
import uploadIcon from 'src/assets/images/icons/upload-file.svg';
import { UCTCardFile } from '../UploadDocument/CardFile';

const axios = createAxiosInstance({}, [attachCommonErrorInterceptor]);

interface ModernUploadDocumentProps {
  invoiceSummary: InvoiceSummaryDto;
  attachmentType: 'APPROVAL' | 'RATE_CARD' | 'CREDIT_NOTE';
}

interface UploadedFile {
  id: string;
  name: string;
  uploadDate: string;
  progress: number;
  attachment?: Attachment | null;
}

interface CurrentUpload {
  name: string;
  progress: number;
}

export const ModernUploadDocument: React.FC<ModernUploadDocumentProps> = ({ invoiceSummary, attachmentType }) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentUpload, setCurrentUpload] = useState<CurrentUpload | null>(null);

  const queryClient = useQueryClient();
  const { mutateAsync: getUploadApprovalDoc } = useUploadApprovalDocument();

  // Load existing attachments from invoiceSummary when component mounts
  useEffect(() => {
    if (invoiceSummary?.invoiceAttachments) {
      const existingFiles: UploadedFile[] = invoiceSummary.invoiceAttachments
        .filter((attachment) => attachment.attachmentType === attachmentType)
        .map((attachment) => ({
          id: attachment.id || `${attachment.fileName}-${Date.now()}`,
          name: attachment.fileName || 'Unknown File',
          uploadDate: attachment.createdOn
            ? new Date(attachment.createdOn).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : 'Unknown Date',
          progress: 100,
          attachment,
        }));

      setUploadedFiles(existingFiles);
    }
  }, [invoiceSummary, attachmentType]);

  /**
   * uploadFile - Uploads file to the provided URL with progress tracking
   */
  const uploadFile = useCallback(async (uploadUrl: string, file: File) => {
    await axios.put(uploadUrl, file, {
      onUploadProgress: (progressEvent) => {
        const percentage = Math.floor(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        setCurrentUpload((prev) => (prev ? { ...prev, progress: percentage } : null));
      },
      method: 'PUT',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }, []);

  /**
   * uploadDocument - Main upload function that handles the complete upload process
   */
  const uploadDocument = useCallback(
    async (file: File) => {
      try {
        setIsUploading(true);
        setCurrentUpload({ name: file.name, progress: 0 });

        // Get upload URL from API
        const uploadUrl = await getUploadApprovalDoc({
          invoiceId: invoiceSummary.id,
          fileName: encodeURI(file.name),
          attachmentType,
        });

        // Upload file to the URL
        await uploadFile(uploadUrl, file);

        // Create new file entry
        const newFile: UploadedFile = {
          id: `${file.name}-${Date.now()}-${crypto.randomUUID()}`,
          name: file.name,
          uploadDate: new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }),
          progress: 100,
          attachment: null,
        };

        // Add to uploaded files list
        setUploadedFiles((prev) => [...prev, newFile]);

        // Invalidate queries to refresh data
        queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceSummary.id));
        queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(invoiceSummary.id));
      } catch (error) {
        console.error('Upload failed:', error);
        // You can add error handling here (e.g., show toast notification)
      } finally {
        setIsUploading(false);
        setCurrentUpload(null);
      }
    },
    [invoiceSummary.id, attachmentType, getUploadApprovalDoc, uploadFile, queryClient]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        uploadDocument(file);
      } else if (rejectedFiles.length > 0) {
        // Handle rejected files (e.g., wrong file type, too large, etc.)
        console.error('File rejected:', rejectedFiles);
      }
    },
    [uploadDocument]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: ACCEPT_FILE,
    disabled: isUploading,
  });

  return (
    <div className='mt-24'>
      <h4 className='text-16 xl:text-18 font-bold text-blue_grey-90 mb-8'>Upload Document</h4>
      <p className='text-14 text-blue_grey-70 mb-16'>You can upload supporting document</p>

      <div className='space-y-16'>
        {/* Upload Button */}
        <div>
          <input {...getInputProps()} />
          <Button
            {...getRootProps()}
            endIcon={<img src={uploadIcon} alt='Upload' className='w-20 h-20' />}
            className='!bg-[#E0F3E6] text-primary hover:!bg-primary/20 !px-20 !py-10 !font-semibold !capitalize'
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </div>

        {/* Current Upload Progress */}
        {currentUpload && (
          <UCTCardFile
            attachment={null}
            name={currentUpload.name}
            uploadPercentage={currentUpload.progress}
            onCancelUpload={() => {
              setIsUploading(false);
              setCurrentUpload(null);
            }}
            showDeleteButton
          />
        )}

        {/* Uploaded Files List */}
        {uploadedFiles.map((file) => (
          <UCTCardFile key={file.id} attachment={file.attachment} name={file.name} dateInfo={`Uploaded on ${file.uploadDate}`} showDeleteButton />
        ))}
      </div>
    </div>
  );
};
